import React from 'react';
import { AppRoute } from '../../routes';
import { Card, List, ListSubheader, Divider, IconButton } from '@mui/material';
import { ReusableTrendsGraph, ReusableTrendsCardHeader } from '../ReusableTrendsGraph';
import { TrendChannelList } from '.';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { isDataLoaded, isDataLoading, isDataError, Data, makeLoadedData } from '../../types/data-providers';
import { useTrendData } from '../../data/DataProviders/TrendDataProvider';
import {
    TrendsGraphContentLoader,
    TrendsListLoader,
    TrendsCardHeaderNotLoaded,
} from '../ReusableTrendsGraph/ReusableTrendCardContentLoader';
import { DataEmptyIndicator } from '../DataEmptyIndicator';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { TrendExportModal } from '../DeviceTrends/TrendExportModal';
import { useSelectedSiteData, isRecordFound } from '../../hooks/data-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { Icon } from '../../lib/icons';
import { Selectors } from '../../selectors';
import { Actions, Thunks } from '../../actions';
import { useAuthHelper } from '../AuthContextProvider';

//import { getTrendlineIdentifier, mergeSimilarKeys } from '../../lib/trends-helpers';
import { DeviceMasterProvider } from '../../data/DataProviders/DeviceMasterProvider';

export const TrendsCard: React.FC<{
    large?: boolean;
    viewTrendviewerAppRoute?: AppRoute;
}> = (props) => {
    const { t } = useLanguageLocale();
    const trendsData = useTrendData();
    const selectedSiteData = useSelectedSiteData();
    const Dispatch = useDispatch();
    const AuthHelper = useAuthHelper();
    const [open, setOpen] = React.useState(false);
    const [data, setData] = React.useState(0);

    const csvUrl = useSelector(Selectors.TrendExport.trendExportUrl);
    const trendsExportLoading = useSelector(Selectors.TrendExport.trendExportLoading);

    const [chartData, setChartData] = React.useState<
        Data<
            Array<{
                id: number;
                channelName: string;
                deviceId: string;
                deviceName: string;
                value: Array<{
                    dateTime: string;
                    value: string;
                    isNewAlarm?: boolean;
                    alarmCount?: number;
                }>;
                unit?: string | null;
                color?: any;
                isSelected?: boolean;
            }>
        >
    >();
    const handleClickOpen = (): void => {
        setOpen(true);
    };
    const handleClose = (): void => {
        setOpen(false);
    };

    const selectedChannel = (
        list: Array<{
            id: number;
            channelName: string;
            deviceId: string;
            deviceName: string;
            value: Array<{
                dateTime: string;
                value: string;
                isNewAlarm?: boolean;
                alarmCount?: number;
            }>;
            unit?: string | null;
            color?: any;
            isSelected?: boolean;
        }>
    ): void => {
        const chartValue = makeLoadedData(list);
        setChartData(chartValue);
    };

    React.useEffect(() => {
        if (csvUrl !== '') window.open(csvUrl, '_self');

        return (): void => {
            Dispatch(Actions.TrendExport.trendExportUnmounted());
        };
    }, [csvUrl, Dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const downloadCSV = (channels: any[], days: number): void => {
        if (isDataLoaded(trendsData) && isRecordFound(selectedSiteData)) {
            Dispatch(Thunks.TrendExport.trendExportData(selectedSiteData.value.id, days, channels, AuthHelper));
        }
    };

    if (props.large) {
        return (
            <>
                <Card style={{ margin: '15px', marginTop: 5, marginBottom: 25, flex: 1 }}>
                    {isDataLoading(trendsData) && (
                        <List
                            disablePadding
                            subheader={
                                <ReusableTrendsCardHeader
                                    title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                    large={props.large}
                                >
                                    <TrendsCardHeaderNotLoaded />
                                </ReusableTrendsCardHeader>
                            }
                        >
                            <TrendsGraphContentLoader style={{ height: '395.5px' }} />
                        </List>
                    )}

                    {isDataLoaded(trendsData) && (
                        <>
                            {trendsData.value.length === 0 && (
                                <List
                                    disablePadding
                                    subheader={
                                        <ReusableTrendsCardHeader
                                            title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                            large={props.large}
                                        >
                                            <TrendsCardHeaderNotLoaded />
                                        </ReusableTrendsCardHeader>
                                    }
                                >
                                    <DataEmptyIndicator
                                        icon="ShowChart"
                                        message={t('DASHBOARD.NO_TRENDS')}
                                        data={trendsData}
                                        isEmpty={trendsData.value.length === 0}
                                        inline
                                    />
                                </List>
                            )}

                            {isDataLoaded(chartData) && trendsData.value.length > 0 && (
                                <ReusableTrendsGraph
                                    large
                                    style={{ height: '400px' }}
                                    trendsData={chartData}
                                    downloadCSV={downloadCSV}
                                    downloadFileName={
                                        isRecordFound(selectedSiteData) ? selectedSiteData.value.name : ''
                                    }
                                    siteId={isRecordFound(selectedSiteData) ? selectedSiteData.value.id : ''}
                                    exportChannel={trendsData}
                                />
                            )}
                        </>
                    )}

                    {isDataError(trendsData) && (
                        <List
                            disablePadding
                            subheader={
                                <ReusableTrendsCardHeader
                                    title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                    large={props.large}
                                >
                                    <TrendsCardHeaderNotLoaded />
                                </ReusableTrendsCardHeader>
                            }
                        >
                            <DataErrorIndicator
                                data={trendsData}
                                message={t('DASHBOARD.ERROR_LOADING_TRENDS')}
                                inline
                            />
                        </List>
                    )}
                </Card>
                <Card style={{ width: '360px', margin: '5px', marginBottom: 25, marginRight: 15 }}>
                    <List
                        disablePadding
                        subheader={
                            <ListSubheader
                                color="primary"
                                component="div"
                                style={{
                                    fontWeight: 600,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.CHANNELS')} ({data}/10)
                            </ListSubheader>
                        }
                    >
                        {isDataLoading(trendsData) && (
                            <>
                                <Divider />
                                <TrendsListLoader />
                            </>
                        )}

                        {isDataError(trendsData) && (
                            <>
                                <Divider />
                                <DataErrorIndicator
                                    data={trendsData}
                                    message={t('DASHBOARD.ERROR_LOADING_CHANNELS')}
                                    inline
                                />
                            </>
                        )}

                        {isDataLoaded(trendsData) && (
                            <>
                                <Divider />
                                <DataEmptyIndicator
                                    icon="List"
                                    message={t('DASHBOARD.NO_CHANNELS')}
                                    data={trendsData}
                                    isEmpty={trendsData.value.length === 0}
                                    inline
                                />

                                <TrendChannelList
                                    trends={trendsData.value}
                                    large={true}
                                    count={setData}
                                    selectedChannel={selectedChannel}
                                />
                            </>
                        )}
                    </List>
                </Card>
            </>
        );
    }
    //console.log('ope',someChannelsEnabled);
    return (
        <>
            <Card data-testid="site-trends-card">
                <List
                    subheader={
                        <>
                            <ReusableTrendsCardHeader
                                title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TITLE')}
                                count={data}
                                large={false}
                            >
                                <IconButton
                                    data-testid="trend-download"
                                    onClick={handleClickOpen}
                                    disabled={trendsExportLoading}
                                    size="large"
                                >
                                    <Icon.GetApp />
                                </IconButton>
                            </ReusableTrendsCardHeader>
                        </>
                    }
                    disablePadding
                    dense
                >
                    {isDataError(trendsData) && (
                        <DataErrorIndicator data={trendsData} message={t('DASHBOARD.ERROR_LOADING_TRENDS')} inline />
                    )}

                    {isDataLoading(trendsData) && (
                        <>
                            <TrendsGraphContentLoader style={{ height: '195.5px' }} />
                            <Divider />
                            <TrendsListLoader />
                        </>
                    )}

                    {isDataLoaded(trendsData) && (
                        <>
                            <DataEmptyIndicator
                                icon="ShowChart"
                                message={t('DASHBOARD.NO_TRENDS')}
                                data={trendsData}
                                isEmpty={trendsData.value.length === 0}
                                inline
                            />

                            {isDataLoaded(chartData) && trendsData.value.length > 0 && (
                                <ReusableTrendsGraph
                                    trendsData={chartData}
                                    downloadCSV={downloadCSV}
                                    downloadFileName={
                                        isRecordFound(selectedSiteData) ? selectedSiteData.value.name : ''
                                    }
                                    // disableDownload={disableDownload}
                                />
                            )}
                            {trendsData.value.length > 0 && (
                                <TrendChannelList
                                    trends={trendsData.value}
                                    viewTrendviewerAppRoute={props.viewTrendviewerAppRoute}
                                    large={false}
                                    count={setData}
                                    selectedChannel={selectedChannel}
                                />
                            )}
                        </>
                    )}
                </List>
                {open && isDataLoaded(trendsData) && (
                    <DeviceMasterProvider>
                        <TrendExportModal
                            open={open}
                            onClose={handleClose}
                            trends={trendsData.value}
                            downloadCSV={downloadCSV}
                            isDevice={undefined}
                        />
                    </DeviceMasterProvider>
                )}
            </Card>
        </>
    );
};
